import React, { useState } from 'react';
import { useShallow } from 'zustand/shallow';
import { useEditorStore } from '../../stores';
import useRectificationStore from '../../stores/useRectificationStore';
import { useToolManager } from '../../hooks/useToolManager';
import NumberInput from '../ui/NumberInput';
import styles from './ToolCard.module.css';

  // Color array for different measurement headers
  const headerColors = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const MeasureReductionToolCard = () => {
  const [measureReduction, setMeasureReduction, 
    preview, setPreviewFlag,
    isRectificationApplied, setIsRectificationApplied] = useRectificationStore(
    useShallow(s => [s.measureReduction, s.setMeasureReduction, 
      s.rectifiedPreview, s.setRectifiedPreview, 
      s.isRectificationApplied, s.setIsRectificationApplied])
  );
  const undoCommand = useRectificationStore(useShallow(s => s.currentRectificationCommand));
  const isAppliedFromDraft = useRectificationStore(useShallow(s => s.isRectificationAppliedFromDraft));

  const { run } = useToolManager();

  const measurements  = measureReduction.measurements;
  const currentIndex  = measureReduction.currentIndex;

  const cPlaceholder = -1.1;
  const mlPlaceholder = -2.2;
  const apPlaceholder = -3.3;
  const falloffPlaceholder = 1.0;

  const addNewMeasure = () => {
    // always read the latest store snapshot to avoid clobbering
    const latest = useRectificationStore.getState().measureReduction;
    const newId = latest.measurements.length + 1;

    const newMeasure = {
      id: newId,
      scan: { circumference: cPlaceholder, medioLat: mlPlaceholder, antPost: apPlaceholder },
      rectified: { circumference: cPlaceholder, medioLat: mlPlaceholder, antPost: apPlaceholder },
      percentage: 0,
      falloff: falloffPlaceholder,
      height: (newId - 1) * 3,
    };

    setMeasureReduction({
      measurements: [...latest.measurements, newMeasure],
      currentIndex: newId - 1,
    });
  };

  const deleteMeasure = () => {
    const latest = useRectificationStore.getState().measureReduction;
    const newMeasurements = latest.measurements.filter((_, i) => i !== latest.currentIndex)
      .map((m, i) => ({ ...m, id: i + 1 }));
    const newIndex = Math.min(latest.currentIndex, Math.max(0, newMeasurements.length - 1));
    setMeasureReduction({ measurements: newMeasurements, currentIndex: newIndex });
  };

  const updateRectifiedValue = (field, value) => {
    const latest = useRectificationStore.getState().measureReduction;
    const v = Math.round((parseFloat(value) || 0) * 10) / 10;

    const updated = latest.measurements.map((m, i) =>
      i === latest.currentIndex
        ? {
            ...m,
            rectified: { ...m.rectified, [field]: v },
            __editedCm: field === 'circumference' ? true : m.__editedCm,
          }
        : m
    );
    setMeasureReduction({ measurements: updated, currentIndex: latest.currentIndex });
  };

  const updatePercentage = (delta) => {
    const latest = useRectificationStore.getState().measureReduction;
    const updated = latest.measurements.map((m, i) =>
      i === latest.currentIndex ? { ...m, percentage: Math.round((m.percentage + delta) * 10) / 10, __editedCm: false } : m
    );
    setMeasureReduction({ measurements: updated, currentIndex: latest.currentIndex });
  };

  const updateFalloff = (value) => {
    const latest = useRectificationStore.getState().measureReduction;
    const v = Math.round((parseFloat(value) || 0) * 10) / 10;
    const updated = latest.measurements.map((m, i) =>
      i === latest.currentIndex ? { ...m, falloff: v } : m
    );
    setMeasureReduction({ measurements: updated, currentIndex: latest.currentIndex });
  };

  const navigateToNext = () => {
    if (measurements.length > 1) {
      const next = (currentIndex + 1) % measurements.length;
      setMeasureReduction({ currentIndex: next }); // keep same measurements
    }
  };

  const applyRectification = () => {
    if(!isRectificationApplied)
      run({tool: 'main_rectification_measure_reduction', mode: 'apply'});
    else if(undoCommand){
      // Reset history up to this command before undoing
      const { resetHistoryUpTo } = useEditorStore.getState();
      resetHistoryUpTo(undoCommand);
      
      //call undo
      undoCommand.undo();
    }
  }

  const isApplied = isRectificationApplied;
  const isLoadedFromDraft = isApplied && isAppliedFromDraft;

  if (measurements.length === 0) {
    return (
      <div className={styles.toolCard}>
        <div className="mb-3">
          <h3 className="my-0 mb-1.5 text-gray-800 text-base font-semibold">Circumference Modification</h3>
          <p className='m-0 text-gray-500 text-xs'>{isApplied ? 'Measure Reduction Applied' : 'Set the zero height on the positive'}</p>
        </div>
        <div className="flex flex-col gap-5 items-center mt-5 mb-1 pt-4 border-t border-gray-200">
          {
            isApplied ? (
              <button className={`px-2 py-1 rounded-md min-w-20 text-white ${
                isLoadedFromDraft
                  ? 'bg-gray-400 cursor-not-allowed opacity-60'
                  : 'bg-red-400 cursor-pointer hover:bg-red-500 hover:text-white'
              }`}
              onClick={isLoadedFromDraft ? undefined : applyRectification}
              disabled={isLoadedFromDraft}
              title={isLoadedFromDraft ? "Cannot unapply measure reduction loaded from draft" : "Unapply measure reduction"}>
                Unapply
              </button>
            ) : (
              <button className={`bg-white text-purple-600 border-2 border-purple-600 px-2.5 py-1.5 rounded-md text-sm font-semibold transition-all duration-200 ease-in-out ${isApplied ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-purple-600 hover:text-white'}`}
              onClick={addNewMeasure} disabled={isApplied}>
                Add a new measure
              </button>
            )
          }

          {/* <p>Apply Measurement sheet</p> */}
        </div>
      </div>
    );
  }

  const currentMeasurement = measurements[currentIndex];
  if(!currentMeasurement) {
    console.log('currentMeasurement is undefined at index:', currentIndex);
    return null; // or handle the case where currentMeasurement is undefined
  }

  return (
    <div className='flex flex-col absolute left-[10px] top-[10px]'>
      <div className='top-[10px] left-[10px] bg-white rounded-md p-2 shadow-[0_2px_8px_rgba(0,0,0,0.15)] w-[400px] z-[1000] max-h-[70vh] overflow-y-auto border border-gray-200'>
        <div className='flex mb-3 justify-between items-center'>
          <h3
            className='font-bold'
            style={{ color: headerColors[currentIndex % headerColors.length] }}
          >
            Measure n°{currentMeasurement?.id} | Height {currentMeasurement?.height} cm
          </h3>
          <button
            onClick={deleteMeasure}
            className='bg-transparent border-0 cursor-pointer p-1'
            title='Delete measure'
          >
            <img src='/tool_card_icons/scoop_recharge_trash_icon.png' alt='Delete' className='w-4 h-4' />
          </button>
        </div>

        <div className='py-4'>
          <table className='w-full border-collapse'>
            <thead>
              <tr>
                <th className='w-1/4 text-left pb-3 font-semibold text-sm'></th>
                <th className='w-1/4 text-center pb-3 font-semibold text-sm'>Scan</th>
                <th className='w-1/4 text-center pb-3 font-semibold text-sm'>Rectified</th>
                <th className='w-1/4 text-center pb-3 font-semibold text-sm'>Percentage</th>
              </tr>
            </thead>

            <tbody>
              {/* Circumference Row */}
              <tr>
                <td className='py-2 text-sm font-medium'>Circumference</td>
                <td className='py-2 px-1 text-center'>
                  <div className='border border-gray-200 rounded-md px-2 py-1.5 text-center text-sm bg-gray-50'>
                    {currentMeasurement.scan.circumference} cm
                  </div>
                </td>
                <td className='py-2 px-1 text-center'>
                  <div className='flex items-center gap-1 border border-purple-600 rounded-md px-2 py-1 w-fit mx-auto'>
                    <input
                      type='number'
                      step='0.1'
                      value={Math.round(currentMeasurement.rectified.circumference * 10) / 10}
                      onChange={(e) => updateRectifiedValue('circumference', e.target.value)}
                      className='border-0 outline-none w-12 text-center text-sm bg-transparent focus:outline-none'
                    />
                    <span className='text-xs'>cm</span>
                  </div>
                </td>
                <td className='py-2 px-1 text-center'>
                  <div className='flex items-center gap-1 border border-purple-600 rounded-md px-2 py-1 w-fit mx-auto'>
                    <input
                      type='number'
                      step='0.1'
                      value={Math.round(currentMeasurement.percentage * 10) / 10}
                      onChange={(e) =>
                        updatePercentage((parseFloat(e.target.value) || 0) - currentMeasurement.percentage)
                      }
                      className='border-0 outline-none w-12 text-center text-sm bg-transparent focus:outline-none'
                    />
                    <span className='text-sm'>%</span>
                  </div>
                </td>
              </tr>

              {/* Medio <> Lat. Row */}
              <tr>
                <td className='py-2 text-sm font-medium'>Medio &lt;&gt; Lat.</td>
                <td className='py-2 px-1 text-center'>
                  <div className='border border-gray-200 rounded-md px-2 py-1.5 text-center text-sm bg-gray-50'>
                    {currentMeasurement.scan.medioLat} cm
                  </div>
                </td>
                <td className='py-2 px-1 text-center'>
                  <div className='border border-gray-200 rounded-md px-2 py-1.5 text-center text-sm bg-gray-50'>
                    {currentMeasurement.rectified.medioLat} cm
                  </div>
                </td>
                <td className='py-2 px-1 text-center'></td>
              </tr>

              {/* Ant. <> Post. Row */}
              <tr>
                <td className='py-2 text-sm font-medium'>Ant. &lt;&gt; Post.</td>
                <td className='py-2 px-1 text-center'>
                  <div className='border border-gray-200 rounded-md px-2 py-1.5 text-center text-sm bg-gray-50'>
                    {currentMeasurement.scan.antPost} cm
                  </div>
                </td>
                <td className='py-2 px-1 text-center'>
                  <div className='border border-gray-200 rounded-md px-2 py-1.5 text-center text-sm bg-gray-50'>
                    {currentMeasurement.rectified.antPost} cm
                  </div>
                </td>
                <td className='py-2 px-1 text-center'></td>
              </tr>

              {/* Falloff Row */}
              <tr>
                <td className='py-2 text-sm font-medium'>Falloff</td>
                <td className='py-2 px-1 text-center'>
                  <div className='border border-gray-200 rounded-md px-2 py-1.5 text-center text-sm bg-gray-50'>
                    -
                  </div>
                </td>
                <td className='py-2 px-1 text-center'>
                  <div className='flex items-center gap-1 border border-purple-600 rounded-md px-2 py-1 w-fit mx-auto'>
                    <input
                      type='number'
                      step='0.1'
                      value={Math.round((currentMeasurement.falloff || 0) * 10) / 10}
                      onChange={(e) => updateFalloff(e.target.value)}
                      className='border-0 outline-none w-12 text-center text-sm bg-transparent focus:outline-none'
                    />
                    <span className='text-xs'>cm</span>
                  </div>
                </td>
                <td className='py-2 px-1 text-center'></td>
              </tr>
            </tbody>
          </table>

          {/* Footer Buttons */}
          <div className='flex justify-between items-center mt-4'>
            <button
              onClick={addNewMeasure}
              className='border-2 border-purple-600 rounded-lg px-4 py-2 bg-gray-100 text-purple-600 cursor-pointer text-sm font-medium'
            >
              Add a new measure
            </button>
            <button
              onClick={navigateToNext}
              className='border-0 rounded-lg px-4 py-2 bg-purple-600 text-white cursor-pointer text-sm font-medium'
            >
              {currentIndex + 1}/{measurements.length}
            </button>
          </div>
        </div>
      </div>
      <div className='flex items-center justify-between mt-4 px-2'>
        <label className="flex items-center cursor-pointer select-none">
          <input
            type="checkbox"
            checked={preview.enabled}
            onChange={() => setPreviewFlag(!preview.enabled)}
            className="sr-only"
          />
          <div className={`w-10 h-6 rounded-full transition-colors duration-200 relative ${preview.enabled ? 'bg-purple-600' : 'bg-gray-300'}`}>
            <div
              className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 z-10 ${preview.enabled ? 'translate-x-4' : 'translate-x-0'}`}
            ></div>
          </div>
          <span className={`ml-3 text-sm font-medium ${preview.enabled ? 'text-gray-700' : 'text-gray-700'}`}>
            Show Preview
          </span>
        </label>

        <button
          className={`px-2 py-1 rounded-md ml-2 min-w-22 text-white text-lg cursor-pointer bg-violet-600 hover:text-white hover:bg-violet-500`}
          onClick={()=> {
            applyRectification();
          }}
        >
          Apply
        </button>
      </div>
    </div>
  );

};

export default MeasureReductionToolCard; 